import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApprovalConfigListRow } from '../../../../core/models/approval/approval-config.model';
import { MapperApprovalConfigService } from '../../../../core/mappers/approval/mapper-approval-config.service';
import { ApiApprovalConfigService } from '../../../../core/services/api/approval/api-approval-config.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalApprovalConfigComponent } from '../../modals/system-settings/modal-approval-config/modal-approval-config.component';

@Component({
  selector: 'app-approval-config',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalApprovalConfigComponent],
  templateUrl: './approval-config.component.html',
  styleUrl: './approval-config.component.css',
})
export class ApprovalConfigComponent implements OnInit {
  private readonly api = inject(ApiApprovalConfigService);
  private readonly mapper = inject(MapperApprovalConfigService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  items = signal<ApprovalConfigListRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  isDeleteConfirmOpen = signal(false);
  selectedConfigId = signal(0);
  deleteTarget = signal<ApprovalConfigListRow | null>(null);

  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  filterEnable = signal<'all' | 'true' | 'false'>('all');

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  ngOnInit(): void {
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedConfigId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: ApprovalConfigListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedConfigId.set(item.id);
    this.isModalOpen.set(true);
  }

  openDeleteConfirm(item: ApprovalConfigListRow): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    this.deleteTarget.set(item);
    this.isDeleteConfirmOpen.set(true);
  }

  closeDeleteConfirm(): void {
    this.isDeleteConfirmOpen.set(false);
    this.deleteTarget.set(null);
  }

  confirmDelete(): void {
    const item = this.deleteTarget();
    if (!item) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      this.closeDeleteConfirm();
      return;
    }

    this.closeDeleteConfirm();
    this.isLoading.set(true);

    this.api.deleteApprovalConfig(item.id, userId, menuId).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'ลบสำเร็จ');
          this.loadTable();
        } else {
          this.toast.error(mapped.message || 'ลบไม่สำเร็จ');
          this.isLoading.set(false);
        }
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาด');
        this.isLoading.set(false);
      },
    });
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedConfigId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_index: number, item: ApprovalConfigListRow): number {
    return item.id;
  }

  onFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadTable(): void {
    this.isLoading.set(true);
    const enable =
      this.filterEnable() === 'all' ? null : this.filterEnable() === 'true';

    this.api.getApprovalConfigForTable(this.currentPage(), this.pageSize(), null, enable).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable(res);
        this.items.set(mapped.items);
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
